import { useEffect, useRef, useState } from 'react'
import { SmartBack } from '../navigation/SmartBack.js'
import { ArrowRight, ChevronDown, FileText, Landmark, IdCard, Camera, Trash2 } from 'lucide-react'
import { apiGet, adminMutate, adminUpload, usePermissions, useToast } from '../admin/primitives.js'
import { Combobox } from '../admin/pickers.js'
import {
  AuthorityImpactModal, AUTHORITY_ACTION_LABEL, AUTHORITY_HINT, AUTHORITY_PERM,
  type AuthorityAction,
} from './OrganisationLot2.js'
import { CropperModal } from '../admin/Cropper.js'
import { uploadPath, type CropFraction } from '../admin/media-staging.js'
import { useConfirm } from '../shell/confirm.js'
import { dureeHumaine, heureCourte } from './PresencePage.js'
import { Link } from '../router.js'
import { humanName } from './format.js'
import { PersonCountersMini } from './CountersPages.js'
import { useMenuPersonne } from './menu-personne.js'
import { cheminPlanningPersonne } from './planning-personne.js'

/**
 * /equipe/rh/salaries/:id — LA fiche salarié canonique (SIRH). Vraie page, pas un
 * drawer (§43). Onglets : Vue d'ensemble · Contrats · Accès · Sources.
 * Tout est LECTURE : aucun bouton « Modifier l'accès » n'existe (§49).
 */

interface PersonDetail {
  person: { id: string; display_name: string; status: string; created_at: string }
  identities: { system: string; external_id: string; status: string; last_seen_at: string; linked_at: string | null; display?: Record<string, unknown> }[]
  contracts: { id: string; job_title: string | null; contract_type: string | null; starts_at: string | null; ends_at: string | null; status: string; missing_since: string | null }[] | null
  contracts_available: boolean
  access: {
    status: string | null; missing: boolean; last_seen_at: string
    first_name: string | null; last_name: string | null; email: string | null
    policies: { id: string; name: string }[]
    groups: { id: string; name: string }[]
    nfc_count: number
  } | null
  access_available: boolean
}

type Tab = 'overview' | 'emploi' | 'temps' | 'remuneration' | 'documents' | 'developpement' | 'permissions' | 'historique'

/* ─── SIRH LOT 1 — le dossier RH (satellites hr_*, jamais dans person) ─── */
interface HrFile {
  profile: { person_id: string; state: string; payroll_id: string | null; hired_at: string | null; left_at: string | null; work_policy: string } | null
  employments: { id: string; kind: string; started_on: string; ended_on: string | null; note: string | null }[]
  position: { assignment_id: string; position_id: string; name: string; effective_from: string } | null
  functions: { assignment_id: string; function_id: string; name: string; effective_from: string }[]
  locations: { assignment_id: string; location_id: string; name: string; code: string; effective_from: string }[]
  managers: { assignment_id: string; manager_person_id: string; display_name: string; kind: string; effective_from: string }[]
  states: { value: string; label: string }[]
}
interface HrReferentiels {
  positions: { id: string; name: string; active: boolean }[]
  functions: { id: string; name: string; active: boolean }[]
  locations: { id: string; name: string; code: string; active: boolean }[]
}

const STATE_PILL: Record<string, string> = {
  ACTIVE: 'lc-pill--ok', ONBOARDING: 'lc-pill--info', PENDING_VALIDATION: 'lc-pill--warning',
  SUSPENDED: 'lc-pill--warning', LEAVING: 'lc-pill--warning', ENDED: 'lc-pill--idle',
  ARCHIVED: 'lc-pill--idle', CANDIDATE: 'lc-pill--info', PRE_HIRE: 'lc-pill--info',
}
const WORK_POLICY_LABELS: Record<string, string> = {
  ONSITE_ONLY: 'Sur site uniquement', REMOTE_ALLOWED: 'Télétravail autorisé', HYBRID: 'Hybride',
}

interface PresenceSession {
  started_at: string
  ended_at: string | null
  state: 'PRESENT' | 'PARTI' | 'A_VERIFIER'
  reason: string | null
  auto_closed: boolean
  shop_name: string
}

/**
 * PHOTO GESTION (V2.1 §156-162) — stockée par Gestion (bytea), recadrée côté
 * serveur, JAMAIS poussée vers UniFi Access ni vers un quelconque système
 * biométrique. Priorité d'avatar : photo Gestion → photo Access → initiales.
 */
function PersonPhoto({ personId, name }: { personId: string; name: string }) {
  const perms = usePermissions()
  const toast = useToast()
  const confirm = useConfirm()
  const canEdit = !perms.loading && perms.permissions.includes('person.link')
  const [version, setVersion] = useState(0)
  const [broken, setBroken] = useState(false)
  const [staged, setStaged] = useState<File | null>(null)
  const [cropOpen, setCropOpen] = useState(false)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const initials = name.split(/\s+/).map((x) => x[0]).join('').slice(0, 2).toUpperCase()
  const src = `/api/v1/rh/people/${personId}/photo${version ? `?v=${version}` : ''}`

  const pick = (f: File | null) => { if (f) { setStaged(f); setCropOpen(true) } }
  const upload = async (crop: CropFraction) => {
    if (!staged) return
    const r = await adminUpload<{ error?: string; message?: string }>('PUT', uploadPath(`/api/v1/rh/people/${personId}/photo`, crop), staged)
    setStaged(null); setCropOpen(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Photo refusée.'); return }
    setBroken(false); setVersion((v) => v + 1)
    toast('ok', 'Photo enregistrée dans Gestion (jamais envoyée à UniFi Access).')
  }
  const remove = async () => {
    if (!(await confirm({ title: 'Retirer la photo ?', message: 'La personne reprendra sa photo UniFi Access si elle existe, sinon ses initiales.', variant: 'standard', confirmLabel: 'Retirer' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('DELETE', `/api/v1/rh/people/${personId}/photo`, {})
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    setBroken(true); setVersion((v) => v + 1)
    toast('ok', 'Photo retirée.')
  }

  return (
    <div className="rh-photo">
      {!broken
        ? <img className="rh-initials rh-initials--lg rh-photo__img" src={src} alt="" onError={() => setBroken(true)} />
        : <span className="rh-initials rh-initials--lg" aria-hidden="true">{initials}</span>}
      {canEdit ? (
        <span className="rh-photo__actions">
          <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" title="Changer la photo" aria-label="Changer la photo" onClick={() => fileRef.current?.click()}><Camera size={14} /></button>
          {!broken ? <button type="button" className="lc-btn lc-btn--ghost lc-btn--icon" title="Retirer la photo" aria-label="Retirer la photo" onClick={() => void remove()}><Trash2 size={14} /></button> : null}
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/heic,image/heif,.heic,.heif" hidden onChange={(e) => pick(e.target.files?.[0] ?? null)} />
        </span>
      ) : null}
      {staged ? (
        <CropperModal open={cropOpen} file={staged} title="Cadrer la photo" round
          onClose={() => setCropOpen(false)} onExited={() => setStaged(null)} onDone={(c) => void upload(c)} />
      ) : null}
    </div>
  )
}

export function RhPersonPage({ id }: { id: string }) {
  const [d, setD] = useState<PersonDetail | null>(null)
  const [tab, setTab] = useState<Tab>('overview')
  const [tech, setTech] = useState(false)
  const [pres, setPres] = useState<PresenceSession[] | null>(null)
  const [hr, setHr] = useState<HrFile | null>(null)
  const [hrVersion, setHrVersion] = useState(0)
  const [timeline, setTimeline] = useState<{ at: string; kind: string; label: string; detail: string | null }[] | null>(null)
  const menu = useMenuPersonne()

  useEffect(() => {
    apiGet<PersonDetail>(`/api/v1/rh/people/${id}`)
      .then((r) => { if ('person' in r) setD(r) })
      .catch(() => undefined)
    apiGet<{ sessions: PresenceSession[] }>(`/api/v1/rh/people/${id}/presence`)
      .then((r) => { if ('sessions' in r) setPres(r.sessions) })
      .catch(() => undefined)
  }, [id])

  useEffect(() => {
    apiGet<HrFile>(`/api/v1/hr/people/${id}/file`)
      .then((r) => { if ('employments' in r) setHr(r) })
      .catch(() => undefined)
    apiGet<{ entries: { at: string; kind: string; label: string; detail: string | null }[] }>(`/api/v1/hr/people/${id}/timeline`)
      .then((r) => { if ('entries' in r) setTimeline(r.entries) })
      .catch(() => undefined)
  }, [id, hrVersion])

  if (!d) return <main className="lc-page lc-page--readable lc-stagger"><p className="lc-hint">Chargement…</p></main>

  const unifi = d.identities.find((i) => i.system === 'UNIFI_ACCESS' && i.status === 'ACTIVE')

  return (
    <main className="lc-page lc-page--readable lc-stagger">
      <SmartBack fallback="/equipe/rh/salaries" />

      <header className="rh-personhead">
        <PersonPhoto personId={d.person.id} name={d.person.display_name} />
        {/* Le menu sur l'identité, pas sur l'en-tête : la photo y monte la
            fenêtre « Cadrer la photo » (portail), dont les gestes remontent
            l'arbre React — un menu s'ouvrirait par-dessus le recadrage. */}
        <div className="lc-stack" {...menu.personne({ id: d.person.id, nom: d.person.display_name, matricule: hr?.profile?.payroll_id, accesLie: !!unifi })}>
          <h1 className="lc-title" style={{ margin: 0 }}>{humanName(d.person.display_name)}</h1>
          <div className="rh-personhead__pills">
            {hr?.profile ? (
              <span className={`lc-pill ${STATE_PILL[hr.profile.state] ?? 'lc-pill--idle'}`}>
                {hr.states.find((s) => s.value === hr.profile!.state)?.label ?? hr.profile.state}
              </span>
            ) : null}
          </div>
          {hr && (hr.position || hr.functions.length > 0 || hr.profile?.payroll_id) ? (
            <div className="rh-personhead__meta">
              {hr.position ? <span><strong>{hr.position.name}</strong></span> : null}
              {hr.functions.length > 0 ? <span>{hr.functions.map((f) => f.name).join(' · ')}</span> : null}
              {hr.profile?.payroll_id ? <span>Matricule <code>{hr.profile.payroll_id}</code></span> : null}
            </div>
          ) : null}
        </div>
      </header>

      <nav className="lc-tabs rh-tabs" aria-label="Sections de la fiche">
        {([['overview', 'Vue d’ensemble'], ['emploi', 'Emploi'], ['temps', 'Temps'], ['remuneration', 'Rémunération'], ['documents', 'Documents'], ['developpement', 'Développement'], ['permissions', 'Permissions'], ['historique', 'Historique']] as [Tab, string][]).map(([t, label]) => (
          <button key={t} className={`lc-tabs__tab${tab === t ? ' lc-tabs__tab--active' : ''}`} aria-pressed={tab === t} onClick={() => setTab(t)}>
            {label}
          </button>
        ))}
      </nav>

      {tab === 'overview' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body lc-panel__body--flush ad-facts">
          <div className="ad-fact"><span className="ad-fact__label">Nom d’usage</span><span className="ad-fact__value">{d.person.display_name}</span></div>
          <div className="ad-fact">
            <span className="ad-fact__label lc-stack"><span>Accès physiques</span><span className="lc-hint">Détail dans l’onglet Permissions.</span></span>
            <span className="ad-fact__value">
              {d.access
                ? <span className={`lc-pill ${d.access.status === 'ACTIVE' ? 'lc-pill--ok' : 'lc-pill--warning'}`}>{d.access.status === 'ACTIVE' ? 'Actif' : `État : ${d.access.status ?? 'inconnu'}`}</span>
                : unifi ? <span className="lc-hint">Détail réservé (permission accès)</span> : <span className="lc-pill lc-pill--idle">Aucun</span>}
            </span>
          </div>
          <div className="ad-fact"><span className="ad-fact__label">Connue de Gestion depuis</span><span className="ad-fact__value" style={{ fontWeight: 400 }}>{new Date(d.person.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}</span></div>
        </div></section>
      ) : null}

      {tab === 'emploi' ? (
        hr
          ? <HrFileSection personId={d.person.id} hr={hr} onChanged={() => setHrVersion((v) => v + 1)} />
          : <section className="lc-panel gd-panel"><div className="lc-panel__body"><p className="lc-hint">Chargement du dossier RH…</p></div></section>
      ) : null}

      {tab === 'remuneration' || tab === 'documents' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          <p className="lc-hint" style={{ margin: 0 }}>
            {tab === 'remuneration'
              ? 'À venir — régimes, pourcentage service et historique de rémunération (Lot 8).'
              : 'À venir — le coffre documentaire du salarié : contrats, bulletins, attestations (Lot 7).'}
          </p>
        </div></section>
      ) : null}

      {tab === 'developpement' ? (
        <PersonSkillsSection personId={d.person.id} />
      ) : null}

      {tab === 'historique' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          {timeline === null ? <p className="lc-hint">Chargement…</p>
            : timeline.length === 0 ? <p className="lc-hint">Aucun événement daté pour cette personne.</p>
            : (
              <div className="rh-timeline">
                {timeline.map((e, i) => (
                  <div className="rh-timeline__entry" key={i}>
                    <span className="rh-timeline__at">{new Date(e.at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Paris' })}</span>
                    <span className="lc-stack">
                      <span>{e.label}</span>
                      {e.detail ? <span className="rh-timeline__detail">{e.detail}</span> : null}
                    </span>
                  </div>
                ))}
              </div>
            )}
        </div></section>
      ) : null}

      {tab === 'temps' ? (
        <>
          <PersonShiftsSection personId={d.person.id} />
          {/* Lot 4.6 (§90) — mini-synthèse des compteurs, lien vers la page complète. */}
          <PersonCountersMini personId={d.person.id} />
          <section className="lc-panel gd-panel" style={{ marginTop: 'var(--sp-4)' }}><div className="lc-panel__body">
            <button className="lc-btn lc-btn--ghost ad-tech-toggle" onClick={() => setTech(!tech)} aria-expanded={tech}>
              <ChevronDown size={14} style={{ transform: tech ? 'rotate(180deg)' : undefined }} aria-hidden="true" /> Pointages — donnée historique (badgeuse)
            </button>
            {tech ? (
              pres === null ? <p className="adm-note">L’historique de pointage est réservé à la permission dédiée.</p>
              : pres.length === 0 ? <p className="lc-hint">Aucun pointage sur les 14 derniers jours.</p>
              : (
                <table className="lc-table" style={{ marginTop: 'var(--sp-3)' }}>
                  <thead><tr><th>Jour</th><th>Arrivée</th><th>Départ</th><th>Durée</th></tr></thead>
                  <tbody>
                    {pres.map((s, i) => {
                      const jour = new Date(s.started_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })
                      const fin = s.ended_at ? heureCourte(s.ended_at) : (s.state === 'PRESENT' ? 'en cours' : '—')
                      const duree = s.ended_at
                        ? dureeHumaine(Math.round((new Date(s.ended_at).getTime() - new Date(s.started_at).getTime()) / 60000))
                        : s.state === 'PRESENT' ? dureeHumaine(Math.round((Date.now() - new Date(s.started_at).getTime()) / 60000)) : '—'
                      return (
                        <tr key={i}>
                          <td style={{ textTransform: 'capitalize' }}>{jour}</td>
                          <td>{heureCourte(s.started_at)}</td>
                          <td>{fin}</td>
                          <td className="lc-hint">{duree}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )
            ) : null}
          </div></section>
        </>
      ) : null}

      {tab === 'emploi' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          <p className="lc-hint" style={{ marginTop: 0 }}>Contrats — source historique (import), en attendant le Contract Studio.</p>
          {!d.contracts_available ? (
            <p className="adm-note"><Landmark size={14} aria-hidden="true" /> Les contrats sont réservés à la permission dédiée.</p>
          ) : d.contracts && d.contracts.length > 0 ? (
            <table className="lc-table"><thead><tr><th>Poste</th><th>Type</th><th>Début</th><th>Fin</th><th>État</th></tr></thead>
              <tbody>{d.contracts.map((c) => (
                <tr key={c.id}>
                  <td>{c.job_title ?? '—'}</td><td>{c.contract_type ?? '—'}</td>
                  <td>{c.starts_at ? new Date(c.starts_at).toLocaleDateString('fr-FR') : '—'}</td>
                  <td>{c.ends_at ? new Date(c.ends_at).toLocaleDateString('fr-FR') : '—'}</td>
                  <td><span className={`lc-pill ${c.status === 'ACTIVE' ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{c.status === 'ACTIVE' ? 'En cours' : c.status === 'UPCOMING' ? 'À venir' : c.status === 'ENDED' ? 'Terminé' : 'Inconnu'}</span></td>
                </tr>
              ))}</tbody></table>
          ) : (
            <p className="adm-note">
              <FileText size={14} aria-hidden="true" /> Aucun contrat importé pour l’instant.
              Le Contract Studio (Lot 7) portera les contrats canoniques.
            </p>
          )}
        </div></section>
      ) : null}

      {tab === 'permissions' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          {!d.access_available ? (
            <p className="adm-note">Le détail des accès est réservé à la permission dédiée.</p>
          ) : !d.access ? (
            <p className="adm-note">
              Aucune identité d’accès n’est liée à cette personne. La liaison
              est OPTIONNELLE — elle se fait dans <Link href="/settings/integrations/skello">Intégrations → Skello</Link> ;
              les identités, badges et droits se gèrent dans <Link href="/securite/controle-acces">Sécurité → Contrôle d’accès</Link>.
            </p>
          ) : (
            <div className="lc-panel__body--flush ad-facts">
              <div className="ad-fact"><span className="ad-fact__label">État du compte</span><span className="ad-fact__value"><span className={`lc-pill ${d.access.status === 'ACTIVE' ? 'lc-pill--ok' : 'lc-pill--warning'}`}>{d.access.status ?? '—'}</span></span></div>
              <div className="ad-fact">
                <span className="ad-fact__label lc-stack"><span>Politiques d’accès</span><span className="lc-hint">Les politiques réelles UniFi — distinctes des groupes.</span></span>
                <span className="ad-fact__value" style={{ fontWeight: 400 }}>{d.access.policies.length > 0 ? d.access.policies.map((p) => p.name).join(' · ') : 'Aucune'}</span>
              </div>
              <div className="ad-fact"><span className="ad-fact__label">Groupes</span><span className="ad-fact__value" style={{ fontWeight: 400 }}>{d.access.groups.length > 0 ? d.access.groups.map((g) => g.name).join(' · ') : 'Aucun'}</span></div>
              <div className="ad-fact"><span className="ad-fact__label">Badges NFC</span><span className="ad-fact__value">{d.access.nfc_count}</span></div>
              <div className="ad-fact">
                <span className="ad-fact__label lc-stack"><span>Automatisation des droits</span><span className="lc-hint">Aucune règle définie — aucune modification n’est appliquée à UniFi Access.</span></span>
                <span className="ad-fact__value"><span className="lc-pill lc-pill--idle">Non configurée</span></span>
              </div>
            </div>
          )}
        </div></section>
      ) : null}

      {tab === 'permissions' ? (
        <section className="lc-panel gd-panel"><div className="lc-panel__body">
          <div className="lc-panel__body--flush ad-facts">
            {d.identities.map((i) => (
              <div className="ad-fact" key={`${i.system}:${i.external_id}`}>
                <span className="ad-fact__label lc-stack">
                  <span>{i.system === 'SKELLO' ? 'Skello' : i.system === 'UNIFI_ACCESS' ? 'UniFi Access' : i.system}</span>
                  <span className="lc-hint">Vue pour la dernière fois {new Date(i.last_seen_at).toLocaleString('fr-FR')}</span>
                </span>
                <span className="ad-fact__value"><span className={`lc-pill ${i.status === 'ACTIVE' ? 'lc-pill--ok' : 'lc-pill--idle'}`}>{i.status === 'ACTIVE' ? 'Reliée' : 'Historique'}</span></span>
              </div>
            ))}
          </div>
          <button className="lc-btn lc-btn--ghost ad-tech-toggle" onClick={() => setTech(!tech)} aria-expanded={tech}>
            <IdCard size={14} aria-hidden="true" /> Détails techniques <ChevronDown size={14} style={{ transform: tech ? 'rotate(180deg)' : undefined }} aria-hidden="true" />
          </button>
          {tech ? (
            <div className="adm-note" style={{ marginTop: 'var(--sp-3)' }}>
              {d.identities.map((i) => (
                <div key={i.system + i.external_id} className="ad-mono" style={{ fontSize: 'var(--fs-12)' }}>
                  {i.system} · id externe {i.external_id}{i.linked_at ? ` · relié le ${new Date(i.linked_at).toLocaleDateString('fr-FR')}` : ''}
                </div>
              ))}
              <div className="ad-mono" style={{ fontSize: 'var(--fs-12)' }}>person {d.person.id}</div>
            </div>
          ) : null}
        </div></section>
      ) : null}
    </main>
  )
}

/**
 * DOSSIER RH (SIRH LOT 1) — le satellite hr_* de la personne. Trois blocs :
 * profil (état, matricule, dates, politique de travail), épisodes d'emploi,
 * affectations OUVERTES (poste, fonctions, lieux, responsable) avec clôture
 * datée — modifier = clore + recréer, l'histoire n'est jamais réécrite.
 */
function HrFileSection({ personId, hr, onChanged }: { personId: string; hr: HrFile; onChanged: () => void }) {
  const perms = usePermissions()
  const toast = useToast()
  const confirm = useConfirm()
  const canEdit = !perms.loading && perms.permissions.includes('hr.person.edit')
  /* LOT 4.8 — poser ou clore un RESPONSABLE déplace un périmètre : ça relève
     de l'autorité, pas de l'édition du dossier. Sans le droit dédié, l'action
     n'est pas affichée (DS §8 : absente, jamais grisée). */
  const canAuthority = !perms.loading && perms.permissions.includes(AUTHORITY_PERM)
  const [authority, setAuthority] = useState<{
    action: AuthorityAction; subjectName: string; title: string; confirmLabel: string
    request: { method: string; path: string; body: Record<string, unknown> }
  } | null>(null)

  const [refs, setRefs] = useState<HrReferentiels | null>(null)
  const [people, setPeople] = useState<{ id: string; display_name: string }[] | null>(null)
  const [profile, setProfile] = useState({
    state: hr.profile?.state ?? 'ACTIVE',
    payroll_id: hr.profile?.payroll_id ?? '',
    hired_at: hr.profile?.hired_at ?? '',
    left_at: hr.profile?.left_at ?? '',
    work_policy: hr.profile?.work_policy ?? 'ONSITE_ONLY',
  })
  const [savingProfile, setSavingProfile] = useState(false)
  const [assign, setAssign] = useState({ kind: 'POSITION', target: '', from: new Date().toISOString().slice(0, 10), reason: '' })
  const [savingAssign, setSavingAssign] = useState(false)
  const [emp, setEmp] = useState({ kind: 'EMPLOYMENT', started_on: '', note: '' })

  useEffect(() => {
    if (!canEdit) return
    apiGet<HrReferentiels>('/api/v1/hr/referentiels').then((r) => { if ('positions' in r) setRefs(r) }).catch(() => undefined)
    apiGet<{ people: { id: string; display_name: string }[] }>('/api/v1/rh/people').then((r) => { if ('people' in r) setPeople(r.people) }).catch(() => undefined)
  }, [canEdit])

  const saveProfile = async () => {
    if (savingProfile) return
    setSavingProfile(true)
    const body: Record<string, unknown> = { state: profile.state, work_policy: profile.work_policy }
    if (profile.payroll_id.trim()) body['payroll_id'] = profile.payroll_id.trim(); else body['clear_payroll_id'] = true
    if (profile.hired_at) body['hired_at'] = profile.hired_at; else body['clear_hired_at'] = true
    if (profile.left_at) body['left_at'] = profile.left_at; else body['clear_left_at'] = true
    const r = await adminMutate<{ error?: string; message?: string }>('PUT', `/api/v1/hr/people/${personId}/profile`, body)
    setSavingProfile(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Enregistrement refusé.'); return }
    toast('ok', 'Dossier RH enregistré.')
    onChanged()
  }

  const addAssignment = async () => {
    if (savingAssign || !assign.target || !assign.from) return
    const body: Record<string, unknown> = { kind: assign.kind, target_id: assign.target, effective_from: assign.from }
    if (assign.reason.trim()) body['reason'] = assign.reason.trim()
    // Rattachement « Responsable » = acte d'autorité : aperçu d'impact d'abord.
    if (assign.kind === 'MANAGER') {
      const nom = (people ?? []).find((p) => p.id === assign.target)?.display_name ?? 'ce responsable'
      setAuthority({
        action: {
          kind: 'MANAGER_ADD', person_id: personId, manager_person_id: assign.target,
          manager_kind: 'PRIMARY', effective_from: assign.from,
        },
        subjectName: nom,
        title: AUTHORITY_ACTION_LABEL['MANAGER_ADD']!,
        confirmLabel: 'Rattacher',
        request: { method: 'POST', path: `/api/v1/hr/people/${personId}/assignments`, body: { ...body, manager_kind: 'PRIMARY' } },
      })
      return
    }
    setSavingAssign(true)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/people/${personId}/assignments`, body)
    setSavingAssign(false)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Affectation refusée.'); return }
    toast('ok', 'Affectation enregistrée.')
    setAssign({ ...assign, target: '', reason: '' })
    onChanged()
  }

  const endAssign = async (kind: string, assignmentId: string, label: string) => {
    const today = new Date().toISOString().slice(0, 10)
    if (kind === 'MANAGER') {
      setAuthority({
        action: { kind: 'MANAGER_END', assignment_id: assignmentId, effective_until: today },
        subjectName: label,
        title: AUTHORITY_ACTION_LABEL['MANAGER_END']!,
        confirmLabel: 'Clore le rattachement',
        request: { method: 'POST', path: `/api/v1/hr/assignments/${assignmentId}/end`, body: { kind: 'MANAGER', effective_until: today } },
      })
      return
    }
    if (!(await confirm({
      title: `Clore « ${label} » ?`,
      message: `L'affectation sera close à effet d'aujourd'hui (${today}). La ligne reste dans l'historique — rien n'est effacé.`,
      variant: 'standard', confirmLabel: 'Clore',
    }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/assignments/${assignmentId}/end`, { kind, effective_until: today })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Affectation close.')
    onChanged()
  }

  const addEmployment = async () => {
    if (!emp.started_on) return
    const body: Record<string, unknown> = { kind: emp.kind, started_on: emp.started_on }
    if (emp.note.trim()) body['note'] = emp.note.trim()
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/people/${personId}/employments`, body)
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', 'Épisode d’emploi enregistré.')
    setEmp({ kind: 'EMPLOYMENT', started_on: '', note: '' })
    onChanged()
  }

  const endEmp = async (eid: string) => {
    const today = new Date().toISOString().slice(0, 10)
    if (!(await confirm({ title: 'Clore cet épisode d’emploi ?', message: `Fin au ${today}. L'historique est conservé.`, variant: 'standard', confirmLabel: 'Clore' }))) return
    const r = await adminMutate<{ error?: string; message?: string }>('POST', `/api/v1/hr/employments/${eid}/end`, { ended_on: today })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    onChanged()
  }

  const assignTargets: { id: string; label: string }[] =
    assign.kind === 'POSITION' ? (refs?.positions ?? []).filter((p) => p.active).map((p) => ({ id: p.id, label: p.name }))
    : assign.kind === 'FUNCTION' ? (refs?.functions ?? []).filter((f) => f.active).map((f) => ({ id: f.id, label: f.name }))
    : assign.kind === 'LOCATION' ? (refs?.locations ?? []).filter((l) => l.active).map((l) => ({ id: l.id, label: l.name }))
    : (people ?? []).filter((p) => p.id !== personId).map((p) => ({ id: p.id, label: p.display_name }))

  const EMP_KINDS: Record<string, string> = { EMPLOYMENT: 'Emploi', EXTRA: 'Extra', INTERNSHIP: 'Stage', APPRENTICESHIP: 'Alternance', OTHER: 'Autre' }

  return (
    <section className="lc-panel gd-panel"><div className="lc-panel__body">
      <div className="rh-file__grid">
        <div className="rh-file__block">
          <h3>Profil RH</h3>
          {hr.profile === null && !canEdit ? <p className="lc-hint">Aucun dossier RH n’a encore été ouvert pour cette personne.</p> : null}
          <div className="lc-field"><span className="lc-label">État</span>
            <Combobox
              value={profile.state} disabled={!canEdit} ariaLabel="État du dossier RH"
              items={hr.states.map((s) => ({ value: s.value, label: s.label }))}
              onChange={(v) => setProfile({ ...profile, state: v ?? profile.state })}
            />
          </div>
          <div className="lc-field"><span className="lc-label">Matricule paie</span>
            <input className="lc-input" value={profile.payroll_id} disabled={!canEdit} placeholder="Clé de rapprochement cabinet" onChange={(e) => setProfile({ ...profile, payroll_id: e.target.value })} />
          </div>
          <div className="lc-field"><span className="lc-label">Entrée</span>
            <input className="lc-input" type="date" value={profile.hired_at} disabled={!canEdit} onChange={(e) => setProfile({ ...profile, hired_at: e.target.value })} />
          </div>
          <div className="lc-field"><span className="lc-label">Sortie</span>
            <input className="lc-input" type="date" value={profile.left_at} disabled={!canEdit} onChange={(e) => setProfile({ ...profile, left_at: e.target.value })} />
          </div>
          <div className="lc-field"><span className="lc-label">Politique de travail</span>
            <Combobox
              value={profile.work_policy} disabled={!canEdit} ariaLabel="Politique de travail"
              items={Object.entries(WORK_POLICY_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              onChange={(v) => setProfile({ ...profile, work_policy: v ?? profile.work_policy })}
            />
          </div>
          {canEdit ? <div><button className="lc-btn lc-btn--primary" disabled={savingProfile} onClick={() => void saveProfile()}>{savingProfile ? 'Enregistrement…' : 'Enregistrer le profil'}</button></div> : null}
        </div>

        <div className="rh-file__block">
          <h3>Affectations en cours</h3>
          {hr.position ? (
            <div className="rh-assign">
              <span className="lc-stack"><strong>{hr.position.name}</strong><span className="rh-assign__meta">Poste principal · effet {hr.position.effective_from}</span></span>
              {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => void endAssign('POSITION', hr.position!.assignment_id, hr.position!.name)}>Clore</button> : null}
            </div>
          ) : <p className="lc-hint">Aucun poste principal.</p>}
          {hr.functions.map((f) => (
            <div className="rh-assign" key={f.assignment_id}>
              <span className="lc-stack"><span>{f.name}</span><span className="rh-assign__meta">Fonction · effet {f.effective_from}</span></span>
              {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => void endAssign('FUNCTION', f.assignment_id, f.name)}>Clore</button> : null}
            </div>
          ))}
          {hr.locations.map((l) => (
            <div className="rh-assign" key={l.assignment_id}>
              <span className="lc-stack"><span>{l.name}</span><span className="rh-assign__meta">Lieu · effet {l.effective_from}</span></span>
              {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => void endAssign('LOCATION', l.assignment_id, l.name)}>Clore</button> : null}
            </div>
          ))}
          {hr.managers.map((m) => (
            <div className="rh-assign" key={m.assignment_id}>
              <span className="lc-stack"><span>{m.display_name}</span><span className="rh-assign__meta">Responsable {m.kind === 'PRIMARY' ? 'principal' : m.kind === 'SECONDARY' ? 'secondaire' : 'opérationnel'} · effet {m.effective_from}</span></span>
              {/* Clore un rattachement « Responsable » est une action d'autorité. */}
              {canAuthority ? <button className="lc-btn lc-btn--ghost" onClick={() => void endAssign('MANAGER', m.assignment_id, m.display_name)}>Clore</button> : null}
            </div>
          ))}

          {canEdit ? (
            <div className="lc-stack" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <span className="lc-label">Nouvelle affectation</span>
              <Combobox
                value={assign.kind}
                items={[
                  { value: 'POSITION', label: 'Poste principal' },
                  { value: 'FUNCTION', label: 'Fonction' },
                  { value: 'LOCATION', label: 'Lieu' },
                  // « Responsable » n'apparaît que pour qui détient l'autorité.
                  ...(canAuthority ? [{ value: 'MANAGER', label: 'Responsable' }] : []),
                ]}
                onChange={(v) => setAssign({ ...assign, kind: v ?? 'POSITION', target: '' })}
                ariaLabel="Nature de l’affectation"
              />
              <Combobox
                value={assign.target || null}
                items={assignTargets.map((t) => ({ value: t.id, label: t.label }))}
                onChange={(v) => setAssign({ ...assign, target: v ?? '' })}
                placeholder="Choisir…" ariaLabel="Cible de l’affectation" clearable
              />
              <input className="lc-input" type="date" value={assign.from} aria-label="Date d’effet" onChange={(e) => setAssign({ ...assign, from: e.target.value })} />
              <input className="lc-input" value={assign.reason} placeholder="Motif (optionnel)" onChange={(e) => setAssign({ ...assign, reason: e.target.value })} />
              {assign.kind === 'MANAGER' ? <span className="org-authority__hint">{AUTHORITY_HINT}</span> : null}
              <div><button className="lc-btn" disabled={savingAssign || !assign.target} onClick={() => void addAssignment()}>Affecter</button></div>
              {assign.kind !== 'MANAGER' && refs !== null && assignTargets.length === 0 ? (
                <span className="lc-hint">Le référentiel est vide — créez d’abord l’entrée dans <a href="/equipe/organisation">Organisation</a>.</span>
              ) : null}
            </div>
          ) : null}
        </div>

        <div className="rh-file__block">
          <h3>Épisodes d’emploi</h3>
          {hr.employments.length === 0 ? <p className="lc-hint">Aucun épisode enregistré. Une personne peut partir puis revenir — chaque épisode est une ligne.</p> : null}
          {hr.employments.map((e) => (
            <div className="rh-assign" key={e.id}>
              <span className="lc-stack">
                <span><strong>{EMP_KINDS[e.kind] ?? e.kind}</strong> · {e.started_on}{e.ended_on ? ` → ${e.ended_on}` : ' (en cours)'}</span>
                {e.note ? <span className="rh-assign__meta">{e.note}</span> : null}
              </span>
              {canEdit && !e.ended_on ? <button className="lc-btn lc-btn--ghost" onClick={() => void endEmp(e.id)}>Clore</button> : null}
            </div>
          ))}
          {canEdit ? (
            <div className="lc-stack" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-2)' }}>
              <span className="lc-label">Nouvel épisode</span>
              <Combobox
                value={emp.kind} ariaLabel="Nature de l’épisode d’emploi"
                items={Object.entries(EMP_KINDS).map(([v, l]) => ({ value: v, label: l }))}
                onChange={(v) => setEmp({ ...emp, kind: v ?? emp.kind })}
              />
              <input className="lc-input" type="date" value={emp.started_on} aria-label="Date de début" onChange={(e) => setEmp({ ...emp, started_on: e.target.value })} />
              <input className="lc-input" value={emp.note} placeholder="Note (optionnel)" onChange={(e) => setEmp({ ...emp, note: e.target.value })} />
              <div><button className="lc-btn" disabled={!emp.started_on} onClick={() => void addEmployment()}>Enregistrer</button></div>
            </div>
          ) : null}
        </div>
      </div>

      <AuthorityImpactModal
        open={authority !== null}
        action={authority?.action ?? null}
        subjectName={authority?.subjectName ?? ''}
        title={authority?.title ?? ''}
        confirmLabel={authority?.confirmLabel ?? 'Confirmer'}
        request={authority?.request ?? null}
        onClose={() => setAuthority(null)}
        onApplied={() => { setAssign({ ...assign, target: '', reason: '' }); onChanged() }}
      />
    </div></section>
  )
}

/**
 * DÉVELOPPEMENT (SIRH LOT 2) — les compétences de la personne, avec niveaux
 * administrables. Changer de niveau = clore l'attribution + en créer une
 * nouvelle : l'histoire des progressions reste lisible.
 */
function PersonSkillsSection({ personId }: { personId: string }) {
  const perms = usePermissions()
  const toast = useToast()
  const canEdit = !perms.loading && perms.permissions.includes('hr.person.edit')
  const [data, setData] = useState<{ levels: { code: string; label: string; rank: number }[]; skills: { id: string; name: string; holders: { person_id: string; level_code: string }[] }[] } | null>(null)
  const [version, setVersion] = useState(0)
  const [pick, setPick] = useState({ skill: '', level: '' })

  useEffect(() => {
    apiGet<typeof data>('/api/v1/hr/skills').then((r) => { if (r && 'skills' in (r as object)) setData(r as never) }).catch(() => undefined)
  }, [version])

  if (!data) return <section className="lc-panel gd-panel"><div className="lc-panel__body"><p className="lc-hint">Chargement…</p></div></section>
  const mine = data.skills
    .map((s) => ({ id: s.id, name: s.name, level: s.holders.find((h) => h.person_id === personId)?.level_code ?? null }))
    .filter((s) => s.level !== null)
  const levelLabel = (code: string) => data.levels.find((l) => l.code === code)?.label ?? code

  const act = async (skillId: string, levelCode: string, end: boolean) => {
    const today = new Date().toISOString().slice(0, 10)
    const r = await adminMutate<{ error?: string; message?: string }>('POST', '/api/v1/hr/skills/assign',
      { person_id: personId, skill_id: skillId, level_code: levelCode || 'DECOUVERTE', effective_from: today, end })
    if (r.status >= 400) { toast('error', r.data.message ?? 'Refusé.'); return }
    toast('ok', end ? 'Compétence retirée.' : 'Niveau enregistré.')
    setVersion((v) => v + 1)
  }

  return (
    <section className="lc-panel gd-panel"><div className="lc-panel__body">
      {mine.length === 0 ? <p className="lc-hint">Aucune compétence attribuée.</p> : (
        <div className="rh-file__block">
          {mine.map((s) => (
            <div className="rh-assign" key={s.id}>
              <span className="lc-stack"><strong>{s.name}</strong><span className="rh-assign__meta">{levelLabel(s.level!)}</span></span>
              {canEdit ? <button className="lc-btn lc-btn--ghost" onClick={() => void act(s.id, s.level!, true)}>Retirer</button> : null}
            </div>
          ))}
        </div>
      )}
      {canEdit ? (
        <div className="lc-stack" style={{ gap: 'var(--sp-2)', marginTop: 'var(--sp-3)', maxWidth: 420 }}>
          <span className="lc-label">Attribuer / changer de niveau</span>
          <Combobox
            value={pick.skill || null} placeholder="Compétence…" ariaLabel="Compétence" clearable
            items={data.skills.map((s) => ({ value: s.id, label: s.name }))}
            onChange={(v) => setPick({ ...pick, skill: v ?? '' })}
          />
          <Combobox
            value={pick.level || null} placeholder="Niveau…" ariaLabel="Niveau" clearable
            items={data.levels.map((l) => ({ value: l.code, label: l.label }))}
            onChange={(v) => setPick({ ...pick, level: v ?? '' })}
          />
          <div><button className="lc-btn" disabled={!pick.skill || !pick.level} onClick={() => void act(pick.skill, pick.level, false)}>Enregistrer</button></div>
          <span className="lc-hint">Le catalogue se gère dans <a href="/equipe/organisation/competences">Organisation → Compétences</a>.</span>
        </div>
      ) : null}
    </div></section>
  )
}


/**
 * TEMPS (Lot 2.5) — les shifts PLANIFIÉS de la personne (vérité SIRH).
 * « Voir son planning » ouvre le planning filtré sur elle (?personne=) ; le
 * lien n'existe pas sans `time.schedule.read`, le droit de la route : jamais grisé.
 */
function PersonShiftsSection({ personId }: { personId: string }) {
  const perms = usePermissions()
  const canPlanning = !perms.loading && perms.permissions.includes('time.schedule.read')
  const [shifts, setShifts] = useState<{ id: string; starts_at: string; ends_at: string; function_name: string | null; location_name: string | null }[] | null>(null)
  useEffect(() => {
    apiGet<{ shifts: { id: string; starts_at: string; ends_at: string; function_name: string | null; location_name: string | null }[] }>(`/api/v1/hr/people/${personId}/shifts`)
      .then((r) => { if ('shifts' in r) setShifts(r.shifts) })
      .catch(() => setShifts([]))
  }, [personId])
  const hm = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { timeZone: 'Europe/Paris', hour: '2-digit', minute: '2-digit' })
  return (
    <section className="lc-panel gd-panel"><div className="lc-panel__body">
      <h2 className="lc-section-title">Planning</h2>
      {shifts === null ? <p className="lc-hint">Chargement…</p>
        : shifts.length === 0 ? (
          <p className="lc-hint">Aucun shift planifié.</p>
        ) : (
          <div className="rh-file__block">
            {shifts.map((s) => (
              <div className="rh-assign" key={s.id}>
                <span className="lc-stack">
                  <span style={{ textTransform: 'capitalize' }}>
                    {new Date(s.starts_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/Paris' })}
                  </span>
                  <span className="rh-assign__meta">{hm(s.starts_at)}–{hm(s.ends_at)}{s.function_name ? ` · ${s.function_name}` : ''}{s.location_name ? ` · ${s.location_name}` : ''}</span>
                </span>
              </div>
            ))}
          </div>
        )}
      {canPlanning ? <Link href={cheminPlanningPersonne(personId)} className="ct-mini__link" style={{ marginTop: 'var(--sp-2)' }}>Voir son planning <ArrowRight size={14} aria-hidden="true" /></Link> : null}
    </div></section>
  )
}
